// AgentOptInsList — Phase 15.6 admin tab on AdminOfferDetail.
//
// Shows every chain agent + their opt-in state for THIS offer + an
// inline override input. When admin saves a lower rate, the cascade
// clamp fires automatically server-side; we surface a confirmation
// before applying so admin sees the impact first.
//
// Visual hierarchy:
//   - "OVERRIDDEN" amber badge when override_at IS NOT NULL (so the
//     row obviously stands out from natural snapshots)
//   - Status pill (Opted in / Opted out / Not opted in)
//   - Rate input is disabled unless agent is actively opted in
//   - Real-time validation (0..100); invalid → rose border + disabled save

import { useEffect, useMemo, useState } from 'react';
import { levelName } from '../lib/levelNames';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useToast } from '@baaligames/shared-ui/lib/toast';

type OptInRow = {
  agent_id: number;
  agent_username: string;
  agent_status: string;
  parent_agent_id: number | null;
  level: number | null;
  opt_id: number | null;
  opted_in_at: string | null;
  opted_out_at: string | null;
  cost_share_pct_snapshot: number | null;
  override_admin_id: number | null;
  override_at: string | null;
  override_reason: string | null;
  override_admin_username: string | null;
  partnership_rate_pct: number | null;
};

export default function AgentOptInsList({ offerId }: { offerId: number }) {
  const { push } = useToast();
  const [rows, setRows] = useState<OptInRow[] | null>(null);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [reasons, setReasons] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function refetch() {
    try {
      const r = await adminApi.get<{ rows: OptInRow[] }>(`/admin/offers/${offerId}/optins`);
      setRows(r.rows);
      // Seed drafts with current snapshot for opted-in rows.
      const seed: Record<number, string> = {};
      for (const row of r.rows) {
        if (row.opt_id && !row.opted_out_at) {
          seed[row.agent_id] = String(row.cost_share_pct_snapshot ?? 0);
        }
      }
      setDrafts(seed);
    } catch (e) {
      setErr((e as ApiError).message || 'Could not load opt-ins');
    }
  }

  useEffect(() => { refetch(); /* eslint-disable-next-line */ }, [offerId]);

  // Per-row real-time validation. Rate must be 0..100.
  const validation = useMemo(() => {
    const out: Record<number, { value: number; valid: boolean; msg: string | null; changed: boolean }> = {};
    if (!rows) return out;
    for (const row of rows) {
      if (!row.opt_id || row.opted_out_at) continue;
      const draft = drafts[row.agent_id];
      const trimmed = String(draft ?? '').trim();
      if (trimmed === '') {
        out[row.agent_id] = { value: 0, valid: false, msg: 'Required', changed: true };
        continue;
      }
      const n = Number(trimmed);
      if (!Number.isFinite(n) || Number.isNaN(n)) {
        out[row.agent_id] = { value: NaN, valid: false, msg: 'Numbers only', changed: true };
        continue;
      }
      if (n < 0 || n > 100) {
        out[row.agent_id] = { value: n, valid: false, msg: '0 to 100', changed: true };
        continue;
      }
      const current = Number(row.cost_share_pct_snapshot ?? 0);
      out[row.agent_id] = { value: n, valid: true, msg: null, changed: n !== current };
    }
    return out;
  }, [rows, drafts]);

  // Phase 5 (2026-05-15): Direct save. Cascade preview is gone —
  // weights are independent under proportional subsidy, so there is
  // nothing to cascade-clamp. One PATCH writes the override and the
  // controller writes the audit row in the same transaction.
  async function doApplyOverride(row: OptInRow) {
    if (!row.opt_id) return;
    const v = validation[row.agent_id];
    if (!v || !v.valid) return;
    setBusy(true); setErr(null);
    try {
      await adminApi.patch(
        `/admin/offers/${offerId}/optins/${row.agent_id}`,
        {
          cost_share_pct: v.value,
          reason: reasons[row.agent_id]?.trim() || null,
        }
      );
      push('success', `Weight saved for @${row.agent_username}.`);
      setReasons((rs) => ({ ...rs, [row.agent_id]: '' }));
      await refetch();
    } catch (e) {
      setErr((e as ApiError).message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  if (rows === null && !err) {
    return (
      <div className="rounded-xl bg-arena-50/40 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-4 text-sm text-arena-600 dark:text-neon-200/60">
        Loading agent opt-ins…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-arena-400 dark:text-neon-200/70">
        Each row is one agent in the chain. Rows marked <strong>OVERRIDDEN</strong>
        were manually set by an admin and differ from the agent's natural
        partnership weight. Under proportional subsidy, weights are
        independent — changing one agent's weight has no effect on
        anyone else's.
      </p>

      {err && (
        <div className="rounded-xl bg-rose-50 dark:bg-rose-900/30
                        ring-1 ring-inset ring-rose-200/60 dark:ring-rose-800/50
                        text-rose-700 dark:text-rose-200 px-4 py-3 text-sm">
          {err}
        </div>
      )}

      <div className="rounded-xl bg-white dark:bg-arena-800/40
                      ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 overflow-hidden">
        {/* Header (desktop only) */}
        <div className="hidden sm:grid grid-cols-[1.4fr_0.9fr_1fr_1.4fr] gap-3
                        px-4 py-2.5 border-b ring-arena-200/60 dark:ring-arena-600/40
                        bg-arena-50/60 dark:bg-arena-800/40
                        text-[10.5px] uppercase tracking-wider font-bold
                        text-arena-600 dark:text-neon-200/55">
          <div>Agent</div>
          <div>Status</div>
          <div>Snapshot</div>
          <div>Override</div>
        </div>
        {rows && rows.length === 0 && (
          <div className="px-4 py-6 text-center text-sm text-arena-600 dark:text-neon-200/60">
            No agents in the chain.
          </div>
        )}
        {rows && rows.map((row) => (
          <OptInRowView
            key={row.agent_id}
            row={row}
            draft={drafts[row.agent_id] ?? ''}
            onChangeDraft={(v) => setDrafts((d) => ({ ...d, [row.agent_id]: v }))}
            reason={reasons[row.agent_id] ?? ''}
            onChangeReason={(v) => setReasons((rs) => ({ ...rs, [row.agent_id]: v }))}
            validation={validation[row.agent_id]}
            busy={busy}
            onSave={() => doApplyOverride(row)}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Row ────────────────────────────────────────────────────

function OptInRowView({
  row, draft, onChangeDraft, reason, onChangeReason, validation, busy, onSave,
}: {
  row: OptInRow;
  draft: string;
  onChangeDraft: (v: string) => void;
  reason: string;
  onChangeReason: (v: string) => void;
  validation: { value: number; valid: boolean; msg: string | null; changed: boolean } | undefined;
  busy: boolean;
  onSave: () => void;
}) {
  const isOptedIn  = !!row.opt_id && !row.opted_out_at;
  const isOptedOut = !!row.opt_id && !!row.opted_out_at;
  const isOverridden = !!row.override_at;
  const invalid = validation && !validation.valid;
  const canSave = isOptedIn && validation && validation.valid && validation.changed && !busy;

  return (
    <div className="px-4 py-3 border-t border-arena-200/40 dark:border-arena-700/40/70 first:border-t-0
                    grid grid-cols-1 sm:grid-cols-[1.4fr_0.9fr_1fr_1.4fr] gap-3 items-center">
      {/* Agent */}
      <div className="flex items-center gap-2 min-w-0">
        <span className="grid place-items-center w-9 h-9 rounded-full
                         bg-arena-100/60 dark:bg-arena-800
                         text-arena-700 dark:text-neon-200/75
                         text-xs font-bold shrink-0">
          @{row.agent_username.slice(0, 1).toUpperCase()}
        </span>
        <div className="min-w-0">
          <div className="text-[13px] font-bold text-neutral-900 dark:text-white truncate">
            @{row.agent_username}
          </div>
          <div className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60">
            #{row.agent_id} · {levelName(row.level ?? '—')} · {row.agent_status}
          </div>
        </div>
      </div>

      {/* Status */}
      <div>
        {isOptedIn && (
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5
                           text-[10.5px] font-bold uppercase tracking-wider
                           bg-emerald-100 text-emerald-800
                           dark:bg-emerald-400/15 dark:text-emerald-300">
            <Icon name="check_circle" size={11} filled /> Opted in
          </span>
        )}
        {isOptedOut && (
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5
                           text-[10.5px] font-bold uppercase tracking-wider
                           bg-rose-100 text-rose-800
                           dark:bg-rose-400/15 dark:text-rose-300">
            <Icon name="block" size={11} filled /> Opted out
          </span>
        )}
        {!row.opt_id && (
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5
                           text-[10.5px] font-bold uppercase tracking-wider
                           bg-arena-200/70 text-arena-700
                           dark:bg-white/5 dark:text-arena-400">
            Not opted in
          </span>
        )}
      </div>

      {/* Snapshot */}
      <div>
        {isOptedIn ? (
          <div className="flex items-center gap-2">
            <span className="font-display font-extrabold text-base text-neutral-900 dark:text-white tabular-nums">
              {Number(row.cost_share_pct_snapshot ?? 0)}%
            </span>
            {isOverridden && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5
                               text-[9.5px] font-bold uppercase tracking-wider
                               bg-amber-100 text-amber-800 ring-1 ring-inset ring-amber-300/60
                               dark:bg-amber-400/15 dark:text-amber-200 dark:border-amber-400/40">
                <Icon name="edit" size={10} filled /> Overridden
              </span>
            )}
          </div>
        ) : row.partnership_rate_pct != null ? (
          <span className="text-[12px] text-arena-600 dark:text-neon-200/55">
            Would snapshot {Number(row.partnership_rate_pct)}%
          </span>
        ) : (
          <span className="text-[12px] text-arena-500 dark:text-neon-200/45">—</span>
        )}
        {isOverridden && row.override_admin_username && (
          <div className="text-[10px] text-amber-700 dark:text-amber-300/85 mt-0.5">
            by @{row.override_admin_username} · {row.override_at && new Date(row.override_at).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Override input + save */}
      {isOptedIn ? (
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="relative">
              <input
                type="number" min={0} max={100} step={0.01}
                value={draft}
                onChange={(e) => onChangeDraft(e.target.value)}
                placeholder="Override %"
                disabled={busy}
                className={`w-full pl-2 pr-6 py-1.5 rounded-lg text-right tabular-nums
                            text-sm font-bold bg-white dark:bg-arena-800
                            text-neutral-900 dark:text-neutral-100 outline-none
                            border ${invalid
                              ? 'border-rose-500 focus:ring-2 focus:ring-rose-400/40'
                              : 'ring-arena-200/60 dark:ring-arena-600/40 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-400/30'}`}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-arena-400 dark:text-neon-200/70 pointer-events-none">%</span>
            </div>
            {invalid && (
              <div className="mt-1 text-[10px] text-rose-600 dark:text-rose-300 leading-tight">{validation!.msg}</div>
            )}
            {validation?.valid && validation.changed && (
              <input
                type="text"
                value={reason}
                onChange={(e) => onChangeReason(e.target.value)}
                placeholder="Reason (optional, audit log)"
                maxLength={190}
                disabled={busy}
                className="mt-1 w-full px-2 py-1 rounded-md text-[11px]
                           bg-arena-50/60 dark:bg-arena-800
                           ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                           text-neutral-900 dark:text-neutral-100"
              />
            )}
          </div>
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave}
            className="shrink-0 px-3 h-9 rounded-lg text-xs font-bold uppercase tracking-wider
                       bg-emerald-600 text-white hover:bg-emerald-500
                       disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            Save
          </button>
        </div>
      ) : (
        <div className="text-[11px] text-arena-500 dark:text-neon-200/45">
          {isOptedOut ? 'Re-opt in via API' : 'Agent must opt in first'}
        </div>
      )}
    </div>
  );
}
